import { Box, ButtonGroup, Checkbox, Typography, useTheme } from '@mui/material'
import Cookies from 'js-cookie'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Slider from 'react-rangeslider'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import type { UnknownAction } from 'redux'

import Accordion from '@/components/Accordion'
import { useAppSelector } from '@/store'
import {
  apiService,
  useDehazeImageFilterMutation,
  useGetCaseDetailsQuery,
  useRecipeImagesUpdateMutation,
} from '@/store/apiService'
import type { CanvasImage } from '@/store/appSlice'
import { PLACEMENT, setDualCanvasImage } from '@/store/appSlice'
import { setGalleryImageLodaingMap } from '@/store/sessionSlice'
import { DEHAZE_OPTIONS } from '@/utils/constants'
import { isNewDehazeEnabled } from '@/utils/helper'

import Button from '../Button'
import Dialog from '../Dialog'

interface DehazingOptionsProps {
  dehazeButton: string
  setDehazeButton: (dehazeButton: string) => void
}

const DehazingOptions = ({
  dehazeButton,
  setDehazeButton,
}: DehazingOptionsProps) => {
  const theme = useTheme()
  const { t } = useTranslation()

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        pt: 0.5,
        width: '100%',
      }}
    >
      <ButtonGroup fullWidth>
        {DEHAZE_OPTIONS?.map((btn, index) => (
          <Button
            key={index}
            variant="outlined"
            onClick={() => setDehazeButton(btn.name)}
            sx={{
              flexGrow: 1,
              bgcolor:
                dehazeButton === btn.name
                  ? theme.palette.primary.main
                  : theme.palette.background.paper,
              color:
                dehazeButton === btn.name
                  ? 'black'
                  : theme.palette.primary.main,
              border: '1px solid rgba(144, 202, 249, 0.50)',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: 24,
              padding: '6px 16px',
              borderRadius: '4px',
              '&:hover': {
                bgcolor:
                  dehazeButton === btn.name
                    ? theme.palette.primary.main
                    : theme.palette.background.paper,
                color:
                  dehazeButton === btn.name
                    ? 'black'
                    : theme.palette.primary.main,
              },
            }}
            label={t(btn.value)}
            textTransform="uppercase"
          />
        ))}
      </ButtonGroup>
    </Box>
  )
}

interface ImageData {
  id: string
  dehazed_image?: string
}

const Dehazing = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const { t } = useTranslation('translation', { keyPrefix: 'gallery' })

  const [dehazeButton, setDehazeButton] = useState('')
  const [brightnessSliderValue, setBrightnessSliderValue] = useState<number>(0)
  const [invertImage, setInvertImage] = useState<boolean>(false)
  const [pollingInterval, setPollingInterval] = useState(0)
  const [dehazingDialog, setDehazingDialog] = useState(false)

  const [selectedImage, setSelectedImage] = useState<ImageData | null>(null)
  const dualCanvasImage = useAppSelector((state) => state.app.dualCanvasImage)
  const isVerticallyAligned = useAppSelector(
    (state) => state.digitalTryIn.isVerticallyAligned,
  )

  const [dehazeImageFilter] = useDehazeImageFilterMutation()
  const caseId = Cookies.get('case_id') || ''

  const { data: caseDetails, refetch } = useGetCaseDetailsQuery(
    { caseId: caseId },
    {
      skip: !caseId,
      pollingInterval,
      skipPollingIfUnfocused: true,
    },
  )

  const [updateRecipeImages] = useRecipeImagesUpdateMutation()

  // Effect to handle polling cleanup
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        setPollingInterval(0)
      }
    }
  }, [pollingInterval])

  useEffect(() => {
    if (caseDetails?.images && selectedImage?.id) {
      const dehazedImage = caseDetails.images.find(
        (image) => image.id === selectedImage.id && isNewDehazeEnabled(image),
      )

      if (dehazedImage && pollingInterval > 0) {
        setPollingInterval(0)
        toast.success(t('dehazeDialog.dehazeSuccess'))

        updateRecipeImages({
          id: selectedImage.id,
          dehazingResetedValue: false,
        }).then((res) => {
          const updatedImage = res.data as CanvasImage
          const updatedDualCanvasImage = { ...dualCanvasImage }

          if (updatedDualCanvasImage[PLACEMENT.LEFT]?.id === updatedImage.id) {
            updatedDualCanvasImage[PLACEMENT.LEFT] = updatedImage
          }

          if (updatedDualCanvasImage[PLACEMENT.RIGHT]?.id === updatedImage.id) {
            updatedDualCanvasImage[PLACEMENT.RIGHT] = updatedImage
          }

          dispatch(setDualCanvasImage(updatedDualCanvasImage))

          dispatch(
            apiService.util.updateQueryData(
              'getCaseDetails',
              { caseId },
              (draft: any) => {
                const idx = draft.images.findIndex(
                  (i: any) => i.id === updatedImage.id,
                )
                if (idx !== -1) {
                  draft.images[idx] = updatedImage
                }
              },
            ) as unknown as UnknownAction,
          )

          dispatch(
            setGalleryImageLodaingMap((prev: any) => ({
              ...prev,
              [updatedImage.id]: false,
            })),
          )
        })
      }
    }
  }, [
    caseDetails,
    selectedImage,
    updateRecipeImages,
    dualCanvasImage,
    dispatch,
  ])

  const dehazeImage = async (placement: PLACEMENT | null) => {
    const image = dualCanvasImage?.[placement as keyof typeof dualCanvasImage]
    if (!image?.id) {
      toast.error(t('dehazeDialog.selectImage'))
      return
    }
    if (!dehazeButton) {
      toast.error(t('dehazeDialog.selectDehazeOption'))
      return
    }
    dispatch(setGalleryImageLodaingMap({ [image.id]: true }))
    setSelectedImage(image)

    try {
      await dehazeImageFilter({
        id: String(image.id),
        haze_mode: dehazeButton,
        brightness: brightnessSliderValue / 100,
        invert: invertImage,
      })

      setPollingInterval(3000)
      setDehazingDialog(false)
      await refetch()
    } catch (error) {
      console.error('Dehazing failed', error)
      setPollingInterval(0)
      setDehazingDialog(false)
      dispatch(setGalleryImageLodaingMap({}))
    }
  }

  return (
    <Accordion
      title={t('dehazing')}
      sx={{
        borderTop: `1px solid ${theme.palette.text.primary}50`,
        px: '16px',
      }}
      accordionDetailsSx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        p: 0,
      }}
      titleSx={{ fontSize: '1.25rem', fontWeight: 400 }}
    >
      <DehazingOptions
        dehazeButton={dehazeButton}
        setDehazeButton={setDehazeButton}
      />
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography
          sx={{ lineHeight: '40px', fontSize: '20px', fontWeight: 400 }}
        >
          {t('brightness')}
        </Typography>
        <Slider
          value={brightnessSliderValue}
          onChange={setBrightnessSliderValue}
          min={0}
          max={100}
          step={10}
          className="slider slider-3"
          orientation="horizontal"
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          sx={{ lineHeight: '40px', fontSize: '20px', fontWeight: 400 }}
        >
          {t('invert')}
        </Typography>
        <Checkbox
          checked={invertImage}
          onChange={(event) => setInvertImage(event.target.checked)}
          sx={{
            width: '1em',
            height: '1em',
          }}
        />
      </Box>

      <Dialog
        triggerElement={
          <Box marginBottom={'41.56px'}>
            <Button
              variant="contained"
              onClick={() => setDehazingDialog(true)}
              sx={{
                flexGrow: 1,
                bgcolor: theme.palette.primary.main,
                color: 'black',
                borderRadius: '4px',
                fontSize: '15px',
                fontWeight: 500,
                lineHeight: '26px',
                width: '100%',
                alignSelf: 'center',
                '&:hover': {
                  bgcolor: theme.palette.primary.main,
                  color: 'black',
                },
              }}
              label={t('apply')}
              textTransform="uppercase"
            />
          </Box>
        }
        isOpen={dehazingDialog}
        onClose={() => setDehazingDialog(false)}
        title={t('dehazeDialog.title')}
        content={null}
        paperStyles={{ width: '600px' }}
        actionButtons={
          <>
            <Button
              variant="contained"
              label={
                isVerticallyAligned
                  ? t('imagePlacementDialog.top')
                  : t('dehazeDialog.left')
              }
              fullWidth
              onClick={async () => dehazeImage(PLACEMENT.LEFT)}
              textTransform="uppercase"
            />
            <Button
              variant="contained"
              label={
                isVerticallyAligned
                  ? t('imagePlacementDialog.bottom')
                  : t('dehazeDialog.right')
              }
              fullWidth
              onClick={async () => dehazeImage(PLACEMENT.RIGHT)}
              textTransform="uppercase"
            />
          </>
        }
      />
    </Accordion>
  )
}

export default Dehazing
